import React, {
    ReactElement, useContext, useEffect, useState,
} from "react";
import { MdNotificationsActive } from "react-icons/md";
import { GrFormClose } from "react-icons/gr";
import { AgentSoundPreference, Permission, UserSoundSettings } from "../__generated__/api";
import usePortal from "./PortalContext";
import { LoggingHandler } from "./logging/LoggingContext";
import useCurrentSession from "./session";
import { isDeepEqual } from "../api";

const LOGO_URL = "https://static.foqal.io/logos/agent/reverse/logo-256.png";
interface NotificationTypes {
    conversation(conversationId: string, title: string, body: string): void;
    message(messageId: string, title: string, body: string): void;
    play(sound: AgentSoundPreference): void;
}

const EXCLUDE_PATHS = [
    "/onboarding",
    "/onboarding/.*",
    "/login",
];

const NOTIFICATION_SOUNDS = {
    [AgentSoundPreference.None]: undefined,
    [AgentSoundPreference.Beyond]: "https://static.foqal.io/knowledge/beyond.mp3",
    [AgentSoundPreference.Bubble]: "https://static.foqal.io/knowledge/bubble.mp3",
    [AgentSoundPreference.LevelUp]: "https://static.foqal.io/knowledge/level-up.mp3",
    [AgentSoundPreference.LiveChat]: "https://static.foqal.io/knowledge/livechat.mp3",
    [AgentSoundPreference.Message]: "https://static.foqal.io/knowledge/message.mp3",
    [AgentSoundPreference.Message_2]: "https://static.foqal.io/knowledge/message-2.mp3",
    [AgentSoundPreference.Message_3]: "https://static.foqal.io/knowledge/message-3.mp3",
    [AgentSoundPreference.Notification]: "https://static.foqal.io/knowledge/notification-email.mp3",
    [AgentSoundPreference.Pop]: "https://static.foqal.io/knowledge/pop.mp3",

};
export const NotificationContext = React.createContext<NotificationTypes>({
    conversation() {
        // do nothing
    },
    message() {
        // do nothing
    },
    play() {
        // nothin
    },
});

class Notifications implements NotificationTypes {
    public static readonly instance = new Notifications();

    private conversationAudio?: HTMLAudioElement;

    private messageAudio?: HTMLAudioElement;

    private lastLoaded: UserSoundSettings = {};

    // eslint-disable-next-line no-useless-constructor
    private constructor() {
        // empty on purpose
    }

    public ensureLoaded(sounds: UserSoundSettings) {
        if (isDeepEqual(this.lastLoaded, sounds)) {
            return;
        }
        this.conversationAudio = new Audio(NOTIFICATION_SOUNDS[sounds.chat ?? AgentSoundPreference.Beyond]);
        this.conversationAudio.load();

        this.messageAudio = new Audio(NOTIFICATION_SOUNDS[sounds.message ?? AgentSoundPreference.Bubble]);
        this.messageAudio.load();
        this.lastLoaded = sounds;
    }

    public static get hasPermission(): boolean {
        return Notification.permission === "granted";
    }

    // eslint-disable-next-line class-methods-use-this
    public play(sound: AgentSoundPreference) {
        const audio = new Audio(NOTIFICATION_SOUNDS[sound]);
        audio.load();
        audio.play().catch(() => {
            // No permissions
        });
    }

    // eslint-disable-next-line class-methods-use-this
    conversation(conversationId: string, title: string, body: string): void {
        this.conversationAudio?.play().catch(() => {
            // do nothing - security error so cant do much on safari.
        });

        if (!Notifications.hasPermission) {
            return;
        }

        LoggingHandler.instance.info({ title }, "Sent new conversation notification");

        // eslint-disable-next-line no-new
        new Notification(title, {
            tag: conversationId,
            body,
            icon: LOGO_URL,
        });
    }

    // eslint-disable-next-line class-methods-use-this
    message(messageId: string, title: string, body: string): void {
        this.messageAudio?.play().catch(() => {
            // do nothing - security error so cant do much on safari.
        });

        LoggingHandler.instance.info({ title }, "Sent message notification");
        if (!Notifications.hasPermission) {
            return;
        }

        // eslint-disable-next-line no-new
        new Notification(title, {
            tag: messageId,
            body,
            icon: LOGO_URL,
        });
    }
}

export const KEY_DISABLE_NOTIFICATION = "disable-notifications";

export function hasDisabledNotifications() {
    return localStorage.getItem(KEY_DISABLE_NOTIFICATION) != null;
}
export function resetNotifications() {
    localStorage.removeItem(KEY_DISABLE_NOTIFICATION);
    window.location.reload();
}

export function NotificationProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [isEnabled, setIsSet] = useState(localStorage.getItem(KEY_DISABLE_NOTIFICATION) != null);
    const { permissions } = usePortal();

    const hasPermissions = permissions.has(Permission.ChatsModify)
        || permissions.has(Permission.Administrator)
        || permissions.has(Permission.TicketsReadSelf);

    const { sounds } = useCurrentSession();
    useEffect(() => {
        if (hasPermissions) {
            Notifications.instance.ensureLoaded(sounds);
        }
    }, [hasPermissions, sounds]);

    const url = new URL(window.location.href);
    const isExcluded = EXCLUDE_PATHS.some(path => new RegExp(path).test(url.pathname));
    if (!hasPermissions || isExcluded) {
        return children;
    }

    const enableNotifications = async () => {
        await Notification.requestPermission();
        setIsSet(true);
    };

    const disableNotifications = async () => {
        localStorage.setItem(KEY_DISABLE_NOTIFICATION, "true");
        setIsSet(true);
    };

    return (
        <NotificationContext.Provider value={Notifications.instance}>
            {(!isEnabled && Notification.permission === "default" && hasPermissions) && (

                <div
                    className={"notifications-bar fixed-top bg-default px-3 py-2 rounded mx-auto d-flex "
                    + `justify-content-between align-items-center text-gray-300`}
                >
                    <div className="flex-grow-1">
                        <MdNotificationsActive />
                        {" "}
                        Please enable notifications so that we can let you know when something new comes in.
                    </div>
                    <button
                        onClick={enableNotifications}
                        type="button"
                        className="btn btn-primary btn-sm"
                    >
                        Enable Notifications
                    </button>
                    <button
                        onClick={disableNotifications}
                        type="button"
                        className="btn btn-none btn-sm ms-3"
                        aria-label="Close notifications window"
                    >
                        <GrFormClose size="14px" />
                    </button>
                </div>
            )}
            {children}
        </NotificationContext.Provider>
    );
}

export default function useNotifications(): NotificationTypes {
    return useContext(NotificationContext) ?? {};
}
